import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  AccountType,
  API_ERROR_MAP,
  AppliedDiscountCodeType,
  BillingType,
  ContactType,
  MembershipType,
  ProductPrices,
  ReferralType,
} from 'types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { selectors } from 'memberships/reducer'
import { selectors as memberSelectors } from 'member-reducer'
import * as apiActions from 'api-actions'
import { flashErrorMessage } from 'redux-flash'
import {
  StripeElementsPaymentForm,
  NoCreditCardRequiredForm,
} from 'member-portal/forms'
import { Spinner } from 'lp-components'
import { StripeProvider } from 'components'
import {
  computeBillingDetails,
  handlePaymentSubmit as submitPayment,
  handleSubmitFailWithFlashMessage,
  hasResolvedCheckoutPrices,
  usePaymentIntentManager,
  useSetupIntentManager,
  withApiAuth,
} from 'utils'

const propTypes = {
  account: AccountType,
  addTrailerCare: PropTypes.func.isRequired,
  billing: BillingType,
  chargeMembershipUpgrade: PropTypes.func.isRequired,
  contact: ContactType,
  deleteStripePaymentIntent: PropTypes.func.isRequired,
  discountCodeDetails: AppliedDiscountCodeType,
  fetchStripePaymentIntent: PropTypes.func.isRequired,
  fetchStripeSetupIntent: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  gclid: PropTypes.string,
  isTrialMembershipsActive: PropTypes.bool,
  membership: MembershipType,
  membershipPrices: ProductPrices,
  membershipProducts: PropTypes.object,
  nextStep: PropTypes.string.isRequired,
  paymentStepType: PropTypes.string.isRequired,
  processPurchase: PropTypes.func.isRequired,
  promoCodeIsTrial: PropTypes.bool,
  referralInfo: ReferralType,
  referredByMemberNumber: PropTypes.string,
  selectedMembershipType: PropTypes.string,
  selectedTrailerCareType: PropTypes.string,
  showTermsAndConditions: PropTypes.bool,
  stripeCustomerId: PropTypes.string,
  testVariant: PropTypes.string,
  trailerCareProducts: PropTypes.object,
  trailerCarePrices: ProductPrices,
  updateStripePaymentIntent: PropTypes.func.isRequired,
}

function StripePaymentStep({
  account = null,
  addTrailerCare,
  billing = null,
  chargeMembershipUpgrade,
  contact = null,
  deleteStripePaymentIntent,
  discountCodeDetails = null,
  fetchStripePaymentIntent,
  fetchStripeSetupIntent,
  flashErrorMessage,
  gclid = null,
  isTrialMembershipsActive,
  membership = null,
  membershipPrices = {},
  membershipProducts = {},
  nextStep,
  paymentStepType,
  processPurchase,
  promoCodeIsTrial,
  referralInfo = null,
  referredByMemberNumber = null,
  selectedMembershipType = null,
  selectedTrailerCareType = null,
  showTermsAndConditions = false,
  stripeCustomerId,
  testVariant = null,
  trailerCareProducts = {},
  trailerCarePrices = {},
  updateStripePaymentIntent,
}) {
  const history = useHistory()
  const [stripePaymentMethodId, setStripePaymentMethodId] = useState(null)
  const [shouldSubmitForm, setShouldSubmitForm] = useState(false)
  const [opportunityGuid, setOpportunityGuid] = useState(null)

  const isActiveTrialCode = isTrialMembershipsActive && promoCodeIsTrial
  const { autoRenewalAgreement } = billing || {}

  // Recompute from the latest prices so a stale/missing price can't slip through.
  const activeBilling = useMemo(() => {
    return computeBillingDetails({
      account,
      billing,
      discountCodeDetails,
      membershipPrices,
      paymentStepType,
      referralCredits: account?.referral_credits__c,
      selectedMembershipType,
      selectedTrailerCareType,
      trailerCarePrices,
      donationAmount: billing?.finalDonation,
    })
  }, [
    account,
    billing,
    discountCodeDetails,
    membershipPrices,
    paymentStepType,
    selectedMembershipType,
    selectedTrailerCareType,
    trailerCarePrices,
  ])

  // No resolvable price leaves the amount empty so no payment intent is created;
  // the flash below surfaces the error.
  const hasValidPricing = hasResolvedCheckoutPrices(activeBilling)
  const finalTotalPayment = hasValidPricing ? activeBilling?.totalPayment : null

  useEffect(() => {
    if (!hasValidPricing)
      flashErrorMessage(API_ERROR_MAP.PRODUCT_PRICES_UNAVAILABLE)
  }, [hasValidPricing, flashErrorMessage])

  // Payment intent management (for charges > $0)
  const {
    paymentIntentId: stripePaymentIntentId,
    clientSecret: paymentClientSecret,
    customerSessionSecret: paymentCustomerSession,
  } = usePaymentIntentManager({
    amount: finalTotalPayment,
    deleteStripePaymentIntent,
    fetchStripePaymentIntent,
    flashErrorMessage,
    stripeCustomerId,
    updateStripePaymentIntent,
  })

  // Setup intent management (for $0 payments with ARB/trial)
  const {
    create: createSetupIntent,
    setupIntentId: stripeSetupIntentId,
    clientSecret: setupClientSecret,
    customerSessionSecret: setupCustomerSession,
  } = useSetupIntentManager({
    accountHerokuId: account?.heroku_external_id__c,
    contactHerokuId: contact?.heroku_external_id__c,
    fetchStripeSetupIntent,
    flashErrorMessage,
    stripeCustomerId,
  })

  const requiresSetupIntent =
    finalTotalPayment != null &&
    finalTotalPayment <= 0 &&
    (isActiveTrialCode || autoRenewalAgreement)

  useEffect(() => {
    if (requiresSetupIntent) {
      createSetupIntent()
    }
  }, [requiresSetupIntent, createSetupIntent])

  const stripeClientSecretId =
    paymentCustomerSession ||
    paymentClientSecret ||
    setupClientSecret ||
    setupCustomerSession
  const showCCForm =
    finalTotalPayment > 0 || isActiveTrialCode || autoRenewalAgreement

  const handlePaymentMethodCreated = (paymentMethodId) => {
    setStripePaymentMethodId(paymentMethodId)
    setShouldSubmitForm(true)
  }

  const handleSubmitFail = (errors, dispatch, submitError) => {
    setShouldSubmitForm(false)
    handleSubmitFailWithFlashMessage(errors, dispatch, submitError)
  }

  const handlePaymentSubmit = (formValues) => {
    if (showCCForm && !stripePaymentMethodId) return

    let billingWithPaymentMethod = {
      ...formValues,
      ...billing,
      ...activeBilling,
    }

    if (showCCForm) {
      billingWithPaymentMethod = {
        ...billingWithPaymentMethod,
        stripeCustomerId,
        stripePaymentIntentId,
        stripePaymentMethodId,
        stripeSetupIntentId,
      }
    }

    const paymentPayload = {
      account,
      billing: billingWithPaymentMethod,
      contact,
      discountCodeDetails,
      gclid,
      isTrial: isActiveTrialCode,
      membership,
      membershipProducts,
      referralInfo,
      referredByMemberNumber,
      selectedMembershipType,
      selectedTrailerCareType,
      testVariant,
      trailerCareProducts,
    }

    return submitPayment({
      addTrailerCare,
      chargeMembershipUpgrade,
      onOpportunityGuid: setOpportunityGuid,
      opportunityGuid,
      payload: paymentPayload,
      paymentStepType,
      processPurchase,
      selectedTrailerCareType,
    })
  }

  // Pricing couldn't be resolved (also flashed above). Show a message in place
  // of the payment form so the member knows why they can't continue.
  if (!hasValidPricing) {
    return (
      <p>
        <em>
          We're having trouble loading your order total. Please refresh the page
          to try again.
        </em>
      </p>
    )
  }

  if (showCCForm && !stripeClientSecretId) return <Spinner />

  if (!showCCForm) {
    return (
      <NoCreditCardRequiredForm
        showTermsAndConditions={showTermsAndConditions}
        onSubmit={handlePaymentSubmit}
        onSubmitSuccess={() => history.push(nextStep)}
        onSubmitFail={handleSubmitFail}
      />
    )
  }

  return (
    <StripeProvider>
      <StripeElementsPaymentForm
        {...{
          account,
          autoRenewalAgreement,
          billing,
          membership,
          onError: flashErrorMessage,
          onPaymentMethodCreated: handlePaymentMethodCreated,
          showTermsAndConditions,
          stripeClientSecretId,
          totalPayment: finalTotalPayment,
          shouldSubmitForm,
        }}
        onSubmit={handlePaymentSubmit}
        onSubmitSuccess={() => history.push(nextStep)}
        onSubmitFail={handleSubmitFail}
      />
    </StripeProvider>
  )
}

StripePaymentStep.propTypes = propTypes

function mapStateToProps(state) {
  return {
    account: memberSelectors.account(state),
    billing: memberSelectors.billing(state),
    contact: memberSelectors.contact(state),
    discountCodeDetails: memberSelectors.discountCodeDetails(state),
    gclid: memberSelectors.gclid(state),
    isTrialMembershipsActive: memberSelectors.isTrialMembershipsActive(state),
    membership: memberSelectors.membership(state),
    membershipProducts: memberSelectors.membershipProducts(state),
    membershipPrices: memberSelectors.membershipPrices(state),
    promoCodeIsTrial: memberSelectors.promoCodeIsTrial(state),
    referralInfo: memberSelectors.referralInfo(state),
    referredByMemberNumber: memberSelectors.referredByMemberNumber(state),
    selectedMembershipType: memberSelectors.selectedMembershipType(state),
    selectedTrailerCareType: memberSelectors.selectedTrailerCareType(state),
    stripeCustomerId: selectors.stripeCustomerId(state),
    testVariant: memberSelectors.testVariant(state),
    trailerCareProducts: memberSelectors.trailerCareProducts(state),
    trailerCarePrices: memberSelectors.trailerCarePrices(state),
  }
}

const mapDispatchToProps = {
  deleteStripePaymentIntent: apiActions.deleteStripePaymentIntent,
  fetchStripePaymentIntent: apiActions.fetchStripePaymentIntent,
  fetchStripeSetupIntent: apiActions.fetchStripeSetupIntent,
  flashErrorMessage,
  processPurchase: apiActions.processPurchase,
  updateStripePaymentIntent: apiActions.updateStripePaymentIntent,
}

const mapApiAuthToProps = {
  addTrailerCare: apiActions.addTrailerCare,
  chargeMembershipUpgrade: apiActions.chargeMembershipUpgrade,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withApiAuth(mapApiAuthToProps),
)(StripePaymentStep)
